/**
 * MeshTransformUtils - Utilities for handling mesh transforms
 * 
 * KEY INSIGHT: InstancedMesh.getAbsolutePosition() is BROKEN in Babylon.js.
 * It returns the SOURCE mesh's position, not the instance's actual position.
 * 
 * SOLUTION: Always use mesh.getWorldMatrix() instead. The world matrix IS correct
 * for both Mesh and InstancedMesh. We just extract position/rotation/scale from it.
 * 
 * This eliminates the need for special InstancedMesh handling throughout the codebase.
 */

import { AbstractMesh, Mesh, InstancedMesh, Vector3, Quaternion, VertexBuffer } from '@babylonjs/core';
import { logger } from '../../core/Logger';

/**
 * Extract world position from a mesh's world matrix.
 * 
 * USE THIS instead of mesh.getAbsolutePosition() - works correctly for InstancedMesh!
 */
export function getWorldPosition(mesh: AbstractMesh): Vector3 {
  mesh.computeWorldMatrix(true);
  const wm = mesh.getWorldMatrix();
  // Position is stored in matrix elements m[12], m[13], m[14]
  return new Vector3(wm.m[12], wm.m[13], wm.m[14]);
}

/**
 * Extract world rotation quaternion from a mesh's world matrix.
 * 
 * USE THIS instead of mesh.absoluteRotationQuaternion - works correctly for InstancedMesh!
 */
export function getWorldRotation(mesh: AbstractMesh): Quaternion {
  mesh.computeWorldMatrix(true);
  const wm = mesh.getWorldMatrix();
  const scale = new Vector3();
  const rotation = new Quaternion();
  const position = new Vector3();
  wm.decompose(scale, rotation, position);
  return rotation;
}

/**
 * Extract world scale from a mesh's world matrix.
 * Returns ABSOLUTE values to handle coordinate flips (negative scale from Blender).
 * 
 * USE THIS instead of mesh.absoluteScaling - works correctly for InstancedMesh!
 */
export function getWorldScale(mesh: AbstractMesh): Vector3 {
  mesh.computeWorldMatrix(true);
  const wm = mesh.getWorldMatrix();
  const scale = new Vector3();
  const rotation = new Quaternion();
  const position = new Vector3();
  wm.decompose(scale, rotation, position);
  // Absolute values to handle coordinate flips (Blender uses -1 scale for coord conversion)
  return new Vector3(Math.abs(scale.x), Math.abs(scale.y), Math.abs(scale.z));
}

/**
 * Extract full world transform from a mesh's world matrix.
 * 
 * USE THIS instead of separate getAbsolutePosition/absoluteRotationQuaternion/absoluteScaling calls.
 * Works correctly for both Mesh and InstancedMesh.
 */
export function getWorldTransform(mesh: AbstractMesh): { position: Vector3; rotation: Quaternion; scale: Vector3 } {
  mesh.computeWorldMatrix(true);
  const wm = mesh.getWorldMatrix();
  const scale = new Vector3();
  const rotation = new Quaternion();
  const position = new Vector3();
  wm.decompose(scale, rotation, position);
  return {
    position,
    rotation,
    // Absolute values for scale
    scale: new Vector3(Math.abs(scale.x), Math.abs(scale.y), Math.abs(scale.z))
  };
}

/**
 * Convert an InstancedMesh to a regular Mesh with correct world transform.
 * The resulting Mesh will have:
 * - Its own unique geometry (not shared with source)
 * - Geometry centered at origin (for physics compatibility)
 * - Position set to keep visual appearance the same
 * 
 * @param instance The InstancedMesh to convert
 * @param options Configuration options
 * @returns A new Mesh with the same appearance
 */
export function convertInstanceToMesh(
  instance: InstancedMesh,
  options: {
    centerGeometry?: boolean;  // Center geometry at origin (default: true)
    disposeInstance?: boolean; // Dispose the original instance (default: true)
    suffix?: string;           // Name suffix for the new mesh (default: '_converted')
  } = {}
): Mesh | null {
  const {
    centerGeometry = true,
    disposeInstance = true,
    suffix = '_converted'
  } = options;

  const sourceMesh = instance.sourceMesh;
  if (!sourceMesh) {
    logger.core.error(`[MeshTransformUtils] Instance ${instance.name} has no source mesh`);
    return null;
  }

  // Get world transform from the instance
  const { position, rotation, scale } = getWorldTransform(instance);

  // Create a new mesh with the source's geometry
  const newMesh = new Mesh(instance.name + suffix, instance.getScene());
  
  // Copy geometry from source mesh
  const sourceGeometry = sourceMesh.geometry;
  if (sourceGeometry) {
    sourceGeometry.applyToMesh(newMesh);
    // Make geometry unique so we can modify vertices without affecting source
    newMesh.makeGeometryUnique();
  }

  // Copy material
  newMesh.material = instance.material || sourceMesh.material;
  newMesh.isVisible = instance.isVisible;
  newMesh.isPickable = instance.isPickable;

  // Apply world transform
  newMesh.position.copyFrom(position);
  newMesh.rotationQuaternion = rotation.clone();
  newMesh.scaling.copyFrom(scale);
  newMesh.computeWorldMatrix(true);

  // Center geometry if requested
  if (centerGeometry) {
    centerMeshGeometry(newMesh);
  }

  // Dispose the original instance
  if (disposeInstance) {
    instance.dispose();
  }

  logger.core.debug(`[MeshTransformUtils] Converted ${instance.name} to Mesh at ${position.toString()}`);

  return newMesh;
}

/**
 * Center a mesh's geometry at the mesh origin.
 * This shifts all vertices so the bounding box center is at (0,0,0),
 * then adjusts the mesh position to keep the visual appearance the same.
 * 
 * This is essential for physics - physics shapes are computed from vertices,
 * and offset vertices cause visual/physics mismatch.
 * 
 * @param mesh The mesh to center
 * @returns The offset that was applied (the original BB center)
 */
export function centerMeshGeometry(mesh: Mesh): Vector3 {
  mesh.refreshBoundingInfo({ applySkeleton: true });
  const bbCenterLocal = mesh.getBoundingInfo().boundingBox.center.clone();

  // Skip if already centered
  if (bbCenterLocal.lengthSquared() < 0.0001) {
    return Vector3.Zero();
  }

  const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
  if (!positions) {
    logger.core.warn(`[MeshTransformUtils] Mesh ${mesh.name} has no position data`);
    return Vector3.Zero();
  }

  // Shift all vertices by negative of local BB center
  const newPositions = new Float32Array(positions.length);
  for (let i = 0; i < positions.length; i += 3) {
    newPositions[i] = positions[i] - bbCenterLocal.x;
    newPositions[i + 1] = positions[i + 1] - bbCenterLocal.y;
    newPositions[i + 2] = positions[i + 2] - bbCenterLocal.z;
  }
  mesh.setVerticesData(VertexBuffer.PositionKind, newPositions);

  // Adjust mesh position to keep geometry in world space
  // Account for scale when computing world offset
  const offsetWorld = bbCenterLocal.multiply(mesh.scaling);
  mesh.position.addInPlace(offsetWorld);

  mesh.refreshBoundingInfo({ applySkeleton: true });
  mesh.computeWorldMatrix(true);

  logger.core.debug(`[MeshTransformUtils] Centered ${mesh.name} geometry, offset=${bbCenterLocal.toString()}`);

  return bbCenterLocal;
}

/**
 * Unparent a mesh while preserving its world transform.
 * Works correctly for both Mesh and InstancedMesh.
 * 
 * @param mesh The mesh to unparent
 */
export function unparentPreservingTransform(mesh: AbstractMesh): void {
  if (!mesh.parent) return;

  // Get world transform BEFORE unparenting
  const { position, rotation, scale } = getWorldTransform(mesh);

  // Unparent
  mesh.setParent(null);

  // Apply world transform
  mesh.position.copyFrom(position);
  mesh.rotationQuaternion = rotation.clone();
  mesh.scaling.copyFrom(scale);
  mesh.computeWorldMatrix(true);
}

/**
 * Prepare a mesh for physics by:
 * 1. Converting InstancedMesh to Mesh if needed
 * 2. Unparenting while preserving world transform
 * 3. Centering geometry at origin
 * 
 * @param mesh The mesh to prepare
 * @returns The prepared Mesh (may be a new object if input was InstancedMesh)
 */
export function prepareMeshForPhysics(mesh: AbstractMesh): Mesh | null {
  let resultMesh: Mesh;

  if (mesh instanceof InstancedMesh) {
    // Convert InstancedMesh to regular Mesh
    const converted = convertInstanceToMesh(mesh, {
      centerGeometry: true,
      disposeInstance: true
    });
    if (!converted) return null;
    resultMesh = converted;
  } else if (mesh instanceof Mesh) {
    // Unparent while preserving transform
    unparentPreservingTransform(mesh);
    
    // Make geometry unique if we need to modify it
    mesh.makeGeometryUnique();
    
    // Center geometry
    centerMeshGeometry(mesh);
    
    resultMesh = mesh;
  } else {
    logger.core.error(`[MeshTransformUtils] Unsupported mesh type: ${mesh.getClassName()}`);
    return null;
  }

  return resultMesh;
}

/**
 * Prepare a debris mesh for physics spawning.
 * Similar to prepareMeshForPhysics but keeps the mesh hidden until spawned.
 * 
 * This converts InstancedMesh to Mesh early, so debris handling is simpler at spawn time.
 * 
 * @param debris The debris mesh (may be InstancedMesh or Mesh)
 * @param parent The parent mesh to stay attached to
 * @returns The prepared Mesh ready for later physics spawning
 */
export function prepareDebrisForSpawning(debris: AbstractMesh, parent: AbstractMesh): Mesh | null {
  let resultMesh: Mesh;

  if (debris instanceof InstancedMesh) {
    // Convert InstancedMesh to regular Mesh while keeping parent relationship
    const sourceMesh = debris.sourceMesh;
    if (!sourceMesh) {
      logger.core.error(`[MeshTransformUtils] Debris ${debris.name} has no source mesh`);
      return null;
    }

    // Get LOCAL position relative to parent (this is what we want to preserve)
    const localPos = debris.position.clone();
    const localRot = debris.rotationQuaternion?.clone() ?? 
      Quaternion.FromEulerAngles(debris.rotation.x, debris.rotation.y, debris.rotation.z);
    const localScale = debris.scaling.clone();

    // Create new mesh with source geometry
    const newMesh = new Mesh(debris.name + '_prepared', debris.getScene());
    
    const sourceGeometry = sourceMesh.geometry;
    if (sourceGeometry) {
      sourceGeometry.applyToMesh(newMesh);
      newMesh.makeGeometryUnique();
    }

    newMesh.material = debris.material || sourceMesh.material;
    
    // Parent to the same parent and set local transform
    newMesh.setParent(parent);
    newMesh.position.copyFrom(localPos);
    newMesh.rotationQuaternion = localRot;
    newMesh.scaling.copyFrom(localScale);

    // Hide until destruction
    newMesh.isVisible = false;
    newMesh.isPickable = false;
    newMesh.setEnabled(false);

    // Dispose original instance
    debris.dispose();

    resultMesh = newMesh;
    logger.core.debug(`[MeshTransformUtils] Prepared debris ${debris.name} (from Instance)`);
  } else if (debris instanceof Mesh) {
    // Already a Mesh, just ensure it's parented correctly
    if (debris.parent !== parent) {
      debris.setParent(parent);
    }
    
    // Make geometry unique for later modification
    debris.makeGeometryUnique();
    
    // Hide until destruction
    debris.isVisible = false;
    debris.isPickable = false;
    debris.setEnabled(false);

    resultMesh = debris;
    logger.core.debug(`[MeshTransformUtils] Prepared debris ${debris.name} (Mesh)`);
  } else {
    logger.core.error(`[MeshTransformUtils] Unsupported debris type: ${debris.getClassName()}`);
    return null;
  }

  return resultMesh;
}

/**
 * Spawn a prepared debris mesh by unparenting and setting up for physics.
 * This is called when the object breaks and debris needs to fly.
 * 
 * @param debris The prepared debris mesh
 * @returns The debris mesh ready for physics body creation
 */
export function spawnDebris(debris: Mesh): Mesh {
  // Get world transform before unparenting
  const { position, rotation, scale } = getWorldTransform(debris);

  // Enable and show
  debris.setEnabled(true);
  debris.isVisible = true;

  // Unparent
  debris.setParent(null);

  // Apply world transform
  debris.position.copyFrom(position);
  debris.rotationQuaternion = rotation.clone();
  debris.scaling.copyFrom(scale);
  debris.computeWorldMatrix(true);

  // Center geometry for physics
  centerMeshGeometry(debris);

  return debris;
}
